import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  IconButton,
  Box,
  ListItemIcon,
  Collapse,
  Typography,
  Stack,
} from '@mui/material';
import {
  Close,
  CheckCircle,
  Error as ErrorIcon,
  CloudUpload as UploadIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ChevronDownIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useCompanyStore } from '@/stores/company-store';
import { useNavigationStore } from '@/stores/navigation-store';
import { useSnackbarStore } from '@certara/common-react-components';
import { validateFilePath } from '@/utils/file-validation';

type WarningType = 'file' | 'folder' | 'both' | 'server';

interface FileUploadStatus {
  file: File;
  path: string;
  status:
    | 'pending'
    | 'uploading'
    | 'success'
    | 'success-with-warning'
    | 'error'
    | 'validation-failed';
  progress: number;
  error?: string;
  warning?: string;
  warningType?: WarningType;
  originalPath?: string;
}

interface UploadProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function Upload({ open: externalOpen, onOpenChange }: UploadProps = {}) {
  const [internalOpen, setInternalOpen] = useState(false);

  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = (newOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  };
  const [files, setFiles] = useState<FileUploadStatus[]>([]);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);

  const { t } = useTranslation('load');
  const { activeCompany } = useCompanyStore();
  const { currentPath } = useNavigationStore();
  const { addSnackbar } = useSnackbarStore();
  const queryClient = useQueryClient();

  const handleClose = () => {
    if (files.some((f) => f.status === 'uploading')) {
      if (!confirm(t('upload.cancelConfirmation'))) {
        return;
      }
    }
    setOpen(false);
    setFiles([]);
    setExpandedItems(new Set());

    if (
      files.some((f) => f.status === 'success' || f.status === 'success-with-warning')
    ) {
      queryClient.invalidateQueries({ queryKey: ['gridData'] });
    }
  };

  const toggleItemExpansion = (fileName: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fileName)) {
        newSet.delete(fileName);
      } else {
        newSet.add(fileName);
      }
      return newSet;
    });
  };

  const getStatusIcon = (status: FileUploadStatus['status']) => {
    switch (status) {
      case 'success':
      case 'success-with-warning':
        return <CheckCircle color="success" fontSize="small" />;
      case 'error':
      case 'validation-failed':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'uploading':
        return <UploadIcon color="primary" fontSize="small" />;
      default:
        return <UploadIcon color="disabled" fontSize="small" />;
    }
  };

  const hasError = (fileStatus: FileUploadStatus) => {
    return (
      (fileStatus.status === 'error' || fileStatus.status === 'validation-failed') &&
      fileStatus.error
    );
  };

  const hasWarning = (fileStatus: FileUploadStatus) => {
    return fileStatus.status === 'success-with-warning' && fileStatus.warning;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);

    if (selectedFiles.length === 0) {
      event.target.value = '';
      return;
    }

    if (!open) {
      setOpen(true);
    }

    const fileStatuses: FileUploadStatus[] = [];
    const sanitizedPathMap = new Map<string, string[]>();

    for (const file of selectedFiles) {
      const relativePath =
        (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;

      const validationResult = validateFilePath(relativePath);

      if (!validationResult.isValid) {
        fileStatuses.push({
          file,
          path: relativePath,
          status: 'validation-failed',
          progress: 0,
          error: validationResult.error,
          originalPath: relativePath,
        });
        continue;
      }

      const sanitizedPath = validationResult.sanitizedName || relativePath;
      const fullPath = currentPath ? `${currentPath}/${sanitizedPath}` : sanitizedPath;

      let warningMessage: string | undefined;
      let warningType: WarningType | undefined;
      if (validationResult.sanitizedName) {
        const originalSegments = relativePath.split('/');
        const sanitizedSegments = sanitizedPath.split('/');

        if (originalSegments.length > 1) {
          const folderChanges: string[] = [];
          const fileChanged =
            originalSegments[originalSegments.length - 1] !==
            sanitizedSegments[sanitizedSegments.length - 1];

          for (let i = 0; i < originalSegments.length - 1; i++) {
            if (originalSegments[i] !== sanitizedSegments[i]) {
              folderChanges.push(`${originalSegments[i]} → ${sanitizedSegments[i]}`);
            }
          }

          const parts: string[] = [];
          if (folderChanges.length > 0) {
            parts.push(t('upload.folderNamesCorrected', { changes: folderChanges.join(', ') }));
          }
          if (fileChanged) {
            parts.push(
              t('upload.fileNameCorrected', {
                original: originalSegments[originalSegments.length - 1],
                corrected: sanitizedSegments[sanitizedSegments.length - 1],
              })
            );
          }

          warningMessage = parts.join('; ');
          if (folderChanges.length > 0 && fileChanged) {
            warningType = 'both';
          } else if (folderChanges.length > 0) {
            warningType = 'folder';
          } else {
            warningType = 'file';
          }
        } else {
          warningMessage = t('upload.fileNameCorrected', { original: relativePath, corrected: sanitizedPath });
          warningType = 'file';
        }
      }

      if (!sanitizedPathMap.has(sanitizedPath)) {
        sanitizedPathMap.set(sanitizedPath, []);
      }
      sanitizedPathMap.get(sanitizedPath)!.push(relativePath);

      fileStatuses.push({
        file,
        path: fullPath,
        status: 'pending' as const,
        progress: 0,
        warning: warningMessage,
        warningType,
        originalPath: relativePath,
      });
    }

    for (const originalPaths of sanitizedPathMap.values()) {
      if (originalPaths.length > 1) {
        for (const originalPath of originalPaths) {
          const fileIndex = fileStatuses.findIndex(
            (f) => f.originalPath === originalPath
          );
          if (fileIndex !== -1) {
            fileStatuses[fileIndex] = {
              ...fileStatuses[fileIndex],
              status: 'validation-failed',
              error: t('upload.sanitizationConflict', { conflicting: originalPaths.filter((p) => p !== originalPath).join(', ') }),
            };
          }
        }
      }
    }

    const validFiles = fileStatuses.filter((f) => f.status === 'pending');

    setFiles(fileStatuses);
    if (validFiles.length > 0) {
      startUpload(validFiles);
    }

    event.target.value = '';
  };

  const startUpload = async (fileStatuses: FileUploadStatus[]) => {
    if (!activeCompany) {
      addSnackbar(t('upload.noCompanySelected'), 'error');
      return;
    }

    for (const fileStatus of fileStatuses) {
      try {
        setFiles((prev) =>
          prev.map((f) =>
            f.file === fileStatus.file ? { ...f, status: 'uploading' } : f
          )
        );

        // Step 1: Start upload (using form data for MVC compatibility)
        type StartFilesUploadResponse = {
          ResponseType: string;
          PartSize: number;
          PartUrls: string[];
          UploadId: string;
          FilePath: string;
          FileName: string;
          ErrorMessage?: string;
        };
        // Default to '/' when currentPath is empty (root level uploads)
        const locationPath = currentPath || '/';

        const startResponse = await apiClient.postForm<StartFilesUploadResponse[]>(
          'api/Storage/StartFilesUpload',
          {
            companyId: activeCompany.Id,
            files: [{ path: fileStatus.path, size: fileStatus.file.size }],
            currentLocationPath: locationPath,
          }
        );

        if (!startResponse) {
          throw new Error(t('hooks.fileUpload.emptyResponse'));
        }

        const uploadInfo = startResponse[0];

        if (!uploadInfo) {
          throw new Error(t('hooks.fileUpload.noUploadInfo'));
        }

        if (uploadInfo.ResponseType?.toLowerCase() !== 'success') {
          throw new Error(
            uploadInfo.ErrorMessage ||
              t('hooks.fileUpload.initFailed', { responseType: uploadInfo.ResponseType })
          );
        }

        const partSize = uploadInfo.PartSize;
        const partUrls = uploadInfo.PartUrls;
        const eTags: string[] = [];

        if (!partUrls || partUrls.length === 0) {
          throw new Error(t('hooks.fileUpload.noUploadUrls'));
        }

        let backendWarning: string | undefined;
        const expectedFileName = fileStatus.path.split('/').pop() || fileStatus.file.name;

        if (uploadInfo.FileName && uploadInfo.FileName !== expectedFileName) {
          backendWarning = t('upload.serverRenamed', { original: expectedFileName, renamed: uploadInfo.FileName });
        } else if (uploadInfo.FilePath) {
          const backendFilename = uploadInfo.FilePath.split('/').pop();
          if (backendFilename && backendFilename !== expectedFileName) {
            backendWarning = t('upload.serverRenamed', { original: expectedFileName, renamed: backendFilename });
          }
        }

        for (let i = 0; i < partUrls.length; i++) {
          const start = i * partSize;
          const end = Math.min(start + partSize, fileStatus.file.size);
          const blob = fileStatus.file.slice(start, end);

          // S3 presigned PUT — plain fetch, NO Authorization header. The URL is
          // signed via query params; S3 rejects requests that also present a
          // bearer ("Only one auth mechanism allowed").
          const uploadResponse = await fetch(partUrls[i], {
            method: 'PUT',
            body: blob,
          });

          if (!uploadResponse.ok) {
            throw new Error(
              t('hooks.fileUpload.partUploadFailed', {
                part: i + 1,
                total: partUrls.length,
                status: uploadResponse.status,
                statusText: uploadResponse.statusText,
              })
            );
          }

          const etag = uploadResponse.headers.get('ETag')?.replace(/"/g, '');
          if (etag) eTags.push(etag);

          const progress = ((i + 1) / partUrls.length) * 100;
          setFiles((prev) =>
            prev.map((f) => (f.file === fileStatus.file ? { ...f, progress } : f))
          );
        }

        await apiClient.postForm('api/Storage/CompleteFileUpload', {
          companyId: activeCompany.Id,
          uploadId: uploadInfo.UploadId,
          path: uploadInfo.FilePath,
          partETags: eTags,
        });

        setFiles((prev) =>
          prev.map((f) => {
            if (f.file === fileStatus.file) {
              const combinedWarning = [f.warning, backendWarning]
                .filter(Boolean)
                .join('; ');
              return {
                ...f,
                status: combinedWarning
                  ? ('success-with-warning' as const)
                  : ('success' as const),
                progress: 100,
                warning: combinedWarning || undefined,
                warningType: backendWarning ? (f.warningType || 'server') : f.warningType,
              };
            }
            return f;
          })
        );
      } catch (error: unknown) {
        let errorMsg = t('hooks.fileUpload.uploadFailed');
        if (error instanceof Error) errorMsg = error.message;

        setFiles((prev) =>
          prev.map((f) =>
            f.file === fileStatus.file ? { ...f, status: 'error', error: errorMsg } : f
          )
        );

        addSnackbar(t('upload.uploadFailed'), 'error', errorMsg);
      }
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      <input
        ref={(el) => {
          folderInputRef.current = el;
          if (el) {
            el.setAttribute('webkitdirectory', '');
            el.setAttribute('directory', '');
          }
        }}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        data-testid="upload-dialog"
      >
        <DialogTitle data-testid="upload-dialog-title">
          {t('upload.title')}
          <IconButton
            data-testid="upload-dialog-close-button"
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {files.length === 0 ? (
            <Box textAlign="center" py={4} data-testid="upload-dialog-file-selection">
              <Button
                data-testid="upload-dialog-select-files-button"
                variant="contained"
                onClick={() => fileInputRef.current?.click()}
                sx={{ mr: 2 }}
              >
                {t('upload.selectFiles')}
              </Button>
              <Button
                variant="contained"
                onClick={() => folderInputRef.current?.click()}
                data-testid="upload-dialog-select-folder-button"
              >
                {t('upload.selectFolder')}
              </Button>
            </Box>
          ) : (
            <>
              {/* Overall Progress */}
              {files.some((f) => f.status === 'uploading' || f.status === 'pending') && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('upload.overallProgress')}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={files.reduce((sum, f) => sum + f.progress, 0) / files.length}
                    sx={{ mb: 0.5, height: 6, borderRadius: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {t('upload.progressSummary', {
                      percent: Math.round(files.reduce((sum, f) => sum + f.progress, 0) / files.length),
                      remaining: files.filter((f) => f.status === 'uploading' || f.status === 'pending').length,
                      total: files.length,
                    })}
                  </Typography>
                </Box>
              )}

              <List dense sx={{ py: 0 }} data-testid="upload-dialog-file-list">
                {files.map((fileStatus, index) => {
                  const fileKey = fileStatus.originalPath || fileStatus.file.name;
                  const isExpanded = expandedItems.has(fileKey);
                  const showExpandButton = hasError(fileStatus) || hasWarning(fileStatus);

                  return (
                    <Box key={index}>
                      <ListItem
                        sx={{
                          py: 1.5,
                          borderBottom: index < files.length - 1 ? 1 : 0,
                          borderColor: 'divider',
                          cursor: showExpandButton ? 'pointer' : 'default',
                          '&:hover': showExpandButton
                            ? {
                                backgroundColor: 'action.hover',
                              }
                            : {},
                        }}
                        onClick={() => showExpandButton && toggleItemExpansion(fileKey)}
                        data-testid={`upload-dialog-file-item-${index}`}
                      >
                        {showExpandButton && (
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            {isExpanded ? (
                              <ChevronDownIcon fontSize="small" color="action" />
                            ) : (
                              <ChevronRightIcon fontSize="small" color="action" />
                            )}
                          </ListItemIcon>
                        )}
                        <ListItemIcon sx={{ minWidth: 36, ml: showExpandButton ? 0 : 3 }}>
                          {getStatusIcon(fileStatus.status)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  maxWidth: showExpandButton ? '350px' : '420px',
                                }}
                                title={fileStatus.originalPath || fileStatus.file.name}
                              >
                                {fileStatus.originalPath || fileStatus.file.name}
                              </Typography>
                              {hasWarning(fileStatus) && (
                                <WarningIcon
                                  fontSize="small"
                                  sx={{ color: 'warning.main' }}
                                />
                              )}
                            </Stack>
                          }
                          secondary={
                            <>
                              {fileStatus.status === 'uploading' && (
                                <>
                                  <LinearProgress
                                    variant="determinate"
                                    value={fileStatus.progress}
                                    sx={{ mb: 0.5, height: 4, borderRadius: 1 }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {Math.round(fileStatus.progress)}%
                                  </Typography>
                                </>
                              )}
                              {fileStatus.status === 'success' && (
                                <Typography variant="caption" color="success.main">
                                  {t('upload.uploadComplete')}
                                </Typography>
                              )}
                              {fileStatus.status === 'success-with-warning' &&
                                !isExpanded && (
                                  <Typography variant="caption" color="warning.main">
                                    {t('upload.uploadedWithModification')}
                                  </Typography>
                                )}
                              {fileStatus.status === 'error' && !isExpanded && (
                                <Typography
                                  variant="caption"
                                  color="error.main"
                                  sx={{
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {fileStatus.error || t('upload.uploadFailed')}
                                </Typography>
                              )}
                              {fileStatus.status === 'validation-failed' &&
                                !isExpanded && (
                                  <Typography
                                    variant="caption"
                                    color="error.main"
                                    sx={{
                                      display: 'block',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {t('upload.invalid', { error: fileStatus.error || t('upload.fileNameNotAllowed') })}
                                  </Typography>
                                )}
                              {fileStatus.status === 'pending' && (
                                <Typography variant="caption" color="text.secondary">
                                  {t('upload.waitingToStart')}
                                </Typography>
                              )}
                            </>
                          }
                          secondaryTypographyProps={{
                            component: 'div',
                            sx: { mt: 0.5 },
                          }}
                          data-testid={`upload-dialog-file-name-${index}`}
                        />
                      </ListItem>

                      {showExpandButton && (
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box
                            sx={{
                              px: 3,
                              py: 2,
                              ml: 6,
                              backgroundColor: hasError(fileStatus)
                                ? 'error.lighter'
                                : 'warning.lighter',
                              borderLeft: 2,
                              borderColor: hasError(fileStatus)
                                ? 'error.main'
                                : 'warning.main',
                            }}
                          >
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              color={hasError(fileStatus) ? 'error.main' : 'warning.main'}
                              display="block"
                              gutterBottom
                            >
                              {hasError(fileStatus)
                                ? fileStatus.status === 'validation-failed'
                                  ? t('upload.validationError')
                                  : t('upload.uploadError')
                                : (() => {
                                    const wt = fileStatus.warningType;
                                    if (wt === 'both') return t('upload.fileFolderNamesModified');
                                    if (wt === 'folder') return t('upload.folderNameModified');
                                    return t('upload.fileNameModified');
                                  })()}
                            </Typography>
                            <Typography
                              variant="caption"
                              color={hasError(fileStatus) ? 'error.dark' : 'warning.dark'}
                              sx={{
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {fileStatus.error || fileStatus.warning}
                            </Typography>
                          </Box>
                        </Collapse>
                      )}
                    </Box>
                  );
                })}
              </List>

              {files.length > 0 &&
                !files.some(
                  (f) => f.status === 'uploading' || f.status === 'pending'
                ) && (
                  <Box
                    sx={{ mt: 2, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}
                  >
                    <Stack direction="row" spacing={2} justifyContent="center">
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <CheckCircle color="success" fontSize="small" />
                        <Typography variant="caption">
                          {t('upload.succeeded', {
                            count: files.filter(
                              (f) =>
                                f.status === 'success' ||
                                f.status === 'success-with-warning'
                            ).length,
                          })}
                        </Typography>
                      </Stack>
                      {files.some(
                        (f) => f.status === 'error' || f.status === 'validation-failed'
                      ) && (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <ErrorIcon color="error" fontSize="small" />
                          <Typography variant="caption">
                            {t('upload.failed', {
                              count: files.filter(
                                (f) =>
                                  f.status === 'error' || f.status === 'validation-failed'
                              ).length,
                            })}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </Box>
                )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} data-testid="upload-dialog-action-button">
            {files.some((f) => f.status === 'uploading') ? t('upload.cancel') : t('upload.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
